import { Box, CircularProgress, TextField } from '@mui/material';
import React from 'react';
import Investor from '../../models/Investor';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { StyledButton } from '../styles/StyledButtons';
import StyledModal from '../styles/StyledModal';
import { StyledTypography } from '../styles/styles';

export const EOISectionSuggestionConfirmModal: React.FC<{
  open: boolean;
  investor: Investor;
  pendingSuggestion: number | null;
  hasSubmitted: boolean;
  suggestionComment: string;
  suggesting: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onCommentChange: (value: string) => void;
}> = ({
  open,
  investor,
  pendingSuggestion,
  hasSubmitted,
  suggestionComment,
  suggesting,
  onClose,
  onConfirm,
  onCommentChange,
}) => (
  <StyledModal open={open} onClose={onClose} title="Confirm Suggestion">
    <Box sx={{ p: 2 }}>
      {pendingSuggestion !== null && (
        <>
          {hasSubmitted && investor.indicativeInvestment ? (
            <StyledTypography sx={{ mb: 2 }}>
              You are suggesting an{' '}
              {pendingSuggestion > investor.indicativeInvestment
                ? 'increase'
                : pendingSuggestion < investor.indicativeInvestment
                  ? 'decrease'
                  : 'update'}{' '}
              of an indicative investment from{' '}
              <strong>
                {ExtendedLiquid.currencyMillions(investor.indicativeInvestment, false)}
              </strong>{' '}
              to <strong>{ExtendedLiquid.currencyMillions(pendingSuggestion, false)}</strong>.
            </StyledTypography>
          ) : (
            <StyledTypography sx={{ mb: 2 }}>
              You are suggesting an indicative investment of{' '}
              <strong>{ExtendedLiquid.currencyMillions(pendingSuggestion, false)}</strong>.
            </StyledTypography>
          )}

          <Box sx={{ mt: 3, mb: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Comment (optional)"
              placeholder="Add any additional comments about this suggestion..."
              value={suggestionComment}
              onChange={(e) => onCommentChange(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Inter, sans-serif',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Inter, sans-serif',
                },
              }}
            />
          </Box>

          <Box display="flex" gap={2} justifyContent="flex-end">
            <StyledButton buttonVariant="inactiveOrCancel" onClick={onClose} disabled={suggesting}>
              Cancel
            </StyledButton>
            <StyledButton onClick={onConfirm} disabled={suggesting}>
              {suggesting && (
                <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />
              )}
              Confirm Suggestion
            </StyledButton>
          </Box>
        </>
      )}
    </Box>
  </StyledModal>
);

export const EOISectionWithdrawSuggestionModal: React.FC<{
  open: boolean;
  investor: Investor;
  withdrawing: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ open, investor, withdrawing, onClose, onConfirm }) => (
  <StyledModal open={open} onClose={onClose} title="Withdraw Suggestion">
    <Box sx={{ p: 2 }}>
      <StyledTypography sx={{ mb: 2 }}>
        Are you sure you want to withdraw your suggestion of{' '}
        <strong>
          {investor.suggestedIndicativeInvestment &&
            ExtendedLiquid.currencyMillions(investor.suggestedIndicativeInvestment, false)}
        </strong>
        ?
      </StyledTypography>
      <StyledTypography sx={{ mb: 3 }}>
        This action cannot be undone. The investor will no longer see your suggestion.
      </StyledTypography>

      <Box display="flex" gap={2} justifyContent="flex-end">
        <StyledButton buttonVariant="inactiveOrCancel" onClick={onClose} disabled={withdrawing}>
          Cancel
        </StyledButton>
        <StyledButton
          buttonVariant="inactiveOrCancel"
          className="destructive"
          onClick={onConfirm}
          disabled={withdrawing}
        >
          {withdrawing && <CircularProgress size={16} sx={{ marginRight: 1, color: 'inherit' }} />}
          Withdraw Suggestion
        </StyledButton>
      </Box>
    </Box>
  </StyledModal>
);
